import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Search, Package, PackagePlus, Wallet, User, LayoutDashboard, Home, Boxes, Menu, LifeBuoy, ShoppingBag, MapPin, LogOut, MessageCircle, Info } from "lucide-react";

export default function MobileTabBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("has-tabbar", !!user);
    return () => document.body.classList.remove("has-tabbar");
  }, [user]);

  if (!user) return null;
  const hideOn = ["/login", "/register"];
  if (hideOn.includes(location.pathname)) return null;

  const params = new URLSearchParams(location.search);
  const curTab = params.get("tab");
  const isActive = (path, tab) => {
    if (path === "/" ) return location.pathname === "/";
    if (location.pathname !== path) return false;
    if (tab) return curTab === tab || (!curTab && tab === "default");
    return true;
  };

  let items;
  if (user.role === "partner") {
    items = [
      { icon: PackagePlus, label: t("mobile.receive"), path: "/dashboard", tab: "receive", def: true },
      { icon: Package, label: t("mobile.shipments"), path: "/dashboard", tab: "shipments" },
      { icon: Wallet, label: t("mobile.earnings"), path: "/dashboard", tab: "earnings" },
      { icon: User, label: t("mobile.profile"), path: "/dashboard", tab: "profile" },
    ];
  } else if (user.role === "admin" || user.role === "super_admin") {
    items = [
      { icon: Search, label: t("mobile.search"), path: "/" },
      { icon: LayoutDashboard, label: t("nav.dashboard"), path: "/dashboard" },
    ];
  } else {
    items = [
      { icon: Home, label: t("mobile.home"), path: "/dashboard", tab: "home", def: true },
      { icon: Search, label: t("mobile.search"), path: "/" },
      { icon: ShoppingBag, label: "Achats", path: "/dashboard", tab: "purchases" },
      { icon: Package, label: t("mobile.shipments"), path: "/dashboard", tab: "shipments" },
      { icon: Menu, label: t("mobile.more"), more: true },
    ];
  }

  const moreLinks = [
    { icon: MapPin, label: "Mes adresses Colis2Go", tab: "addresses" },
    { icon: User, label: "Profil", tab: "profile" },
  ];

  return (
    <>
      {moreOpen && (
        <div data-testid="mobile-more-panel" className="md:hidden fixed bottom-14 inset-x-0 z-50 bg-primary border-t border-white/10 p-2 grid grid-cols-3 gap-1"
          style={{ marginBottom: "env(safe-area-inset-bottom)" }}>
          {moreLinks.map((l) => (
            <button key={l.tab} data-testid={`mmore-${l.tab}`} onClick={() => { setMoreOpen(false); navigate(`/dashboard?tab=${l.tab}`); }}
              className="flex flex-col items-center gap-1 py-3 text-[11px] font-semibold text-white/80 hover:text-accent">
              <l.icon className="h-5 w-5" /> {l.label}
            </button>
          ))}
          <a data-testid="mmore-whatsapp" href={`https://wa.me/447466398452?text=${encodeURIComponent("Bonjour Colis2Go, j'ai besoin d'aide.")}`}
            target="_blank" rel="noreferrer" onClick={() => setMoreOpen(false)}
            className="flex flex-col items-center gap-1 py-3 text-[11px] font-semibold text-white/80 hover:text-accent text-center">
            <MessageCircle className="h-5 w-5 text-[#25D366]" /> Nous contacter sur WhatsApp
          </a>
          <button data-testid="mmore-about" onClick={() => { setMoreOpen(false); navigate("/"); }}
            className="flex flex-col items-center gap-1 py-3 text-[11px] font-semibold text-white/80 hover:text-accent">
            <Info className="h-5 w-5" /> À propos
          </button>
          {user.role === "customer" && (
            <button data-testid="mmore-logout" onClick={async () => { setMoreOpen(false); await logout(); navigate("/"); }}
              className="flex flex-col items-center gap-1 py-3 text-[11px] font-semibold text-white/80 hover:text-accent">
              <LogOut className="h-5 w-5" /> Se déconnecter
            </button>
          )}
        </div>
      )}
      <nav data-testid="mobile-tabbar" className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-primary border-t border-white/10"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
          {items.map((it) => {
            const active = it.more ? moreOpen : (it.path === "/dashboard"
              ? location.pathname === "/dashboard" && (it.tab ? (curTab === it.tab || (!curTab && it.def)) : true)
              : isActive(it.path, it.tab));
            const Icon = it.icon;
            return (
              <button key={it.label} data-testid={`mtab-${it.more ? "more" : it.tab || (it.path === "/" ? "search" : it.path.replace("/", "")) || "home"}`}
                onClick={() => { if (it.more) { setMoreOpen(!moreOpen); } else { setMoreOpen(false); navigate(it.tab ? `${it.path}?tab=${it.tab}` : it.path); } }}
                className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold transition-colors ${active ? "text-accent" : "text-white/60"}`}>
                <Icon className="h-5 w-5" />
                {it.label}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
